import Auth0 from 'auth0-js';
import axios from 'axios';
import has from 'lodash.has';
import get from 'lodash.get';
import jwtDecode from 'jwt-decode';
import constants from '../constants/constants';
import Logger from '../support/logger';
import parseHash from '../support/parse_hash';

const { cookieDomain, setcookie } = window;
const log = new Logger();

export default {
  data: {
    auth0: null,
    hash: parseHash(),
    profile: null
  },
  methods: {
    checkForExistingUser() {
      // Check if Auth0 script will be merging with existing account with same email
      const existingAdminUri = process.env.TRIALS.V2_CHECK_URL.replace('{0}', this.getEmail());

      return axios.get(existingAdminUri)
        .then((isExistingAdmin) => {
          if (isExistingAdmin && isExistingAdmin.data) {
            this.errorObj = {
              status: 422,
              message: 'Merging Google account with existing account'
            };

            return;
          }

          this.errorObj = {
            status: 404,
            message: 'No tenant ID'
          };
        })
        .catch(() => {
          this.errorObj = {
            status: 401,
            message: 'There was an error checking for an existing user'
          };
        });
    },
    getEmail() {
      log.debug({
        category: 'sso',
        message: 'getEmail'
      });

      return this.profile ? this.profile.email : null;
    },
    getGoogleAuth0UserId() {
      log.debug({
        category: 'sso',
        message: 'getGoogleAuth0UserId'
      });

      return this.profile ? this.profile.sub : null;
    },
    getTenant() {
      log.debug({
        category: 'sso',
        message: 'getTenant'
      });

      return get(this.profile, 'https://api.material.com/tenant');
    },
    handleSsoRedirect() {
      log.debug({
        category: 'sso',
        message: 'handleSsoRedirect'
      });

      this.profile = has(this.hash, 'id_token') ? jwtDecode(this.hash.id_token) : null;

      if (this.isSsoError() || this.isMissingTokens() || this.isMissingState() || this.isInvalidProfile()) {
        return;
      }

      if (this.isGoogleUser() && !this.isMissingTenant()) {
        return this.mergeAccounts();
      }

      if (this.isGoogleUser() && this.isMissingTenant()) {
        return this.checkForExistingUser();
      }

      if (this.isPartiallyMergedGoogleUser()) {
        this.errorObj = {
          status: 422,
          message: 'Merging Google account with existing account'
        };

        return;
      }

      if (this.isMissingTenant()) {
        this.errorObj = {
          status: 404,
          message: 'No tenant ID'
        };

        return;
      }

      // passing id_token as access_token is a hack
      this.recordActivity(this.hash.id_token, this.hash.id_token);
    },
    hasMergeTokens(mergeResponseData) {
      log.debug({
        category: 'sso',
        message: 'hasMergeTokens'
      });

      return has(mergeResponseData, 'token.access_token') && has(mergeResponseData, 'token.id_token');
    },
    isGoogleUser() {
      log.debug({
        category: 'sso',
        message: 'isGoogleUser'
      });

      return this.profile && this.profile.sub && this.profile.sub.startsWith('google');
    },
    isInvalidProfile() {
      log.debug({
        category: 'sso',
        message: 'isInvalidProfile'
      });

      if (!this.profile) {
        this.errorObj = {
          status: 401,
          message: 'There was an error parsing the JWT'
        };
        return true;
      }

      return false;
    },
    isMissingState() {
      log.debug({
        category: 'sso',
        message: 'isMissingState'
      });

      if (!has(this.hash, 'state')) {
        this.errorObj = {
          status: 401,
          message: 'Missing state in Auth0 hash'
        };
        return true;
      }

      return false;
    },
    isMissingTenant() {
      log.debug({
        category: 'sso',
        message: 'isMissingTenant'
      });

      return this.profile && !has(this.profile, 'https://api.material.com/tenant');
    },
    isMissingTokens() {
      log.debug({
        category: 'sso',
        message: 'isMissingTokens'
      });

      if (!has(this.hash, 'id_token') || !has(this.hash, 'access_token')) {
        this.errorObj = {
          status: 401,
          message: 'Missing required tokens'
        };
        return true;
      }

      return false;
    },
    isPartiallyMergedGoogleUser() {
      log.debug({
        category: 'sso',
        message: 'isPartiallyMergedGoogleUser'
      });

      /**
       * an email address in the 'name' field of the JWT comes from email/pass account
       * an email address in the 'email' field comes from google SSO
       * if the user has both of these and is missing a tenant, the Auth0 script has merged the accounts already
       * but has not yet added the tenant to the JWT
       */
      const hasNameAndEmail = this.profile.name && this.profile.email;
      return hasNameAndEmail && this.profile.name === this.profile.email && this.isMissingTenant();
    },
    isSsoError() {
      log.debug({
        category: 'sso',
        message: 'isSsoError'
      });

      if (has(this.hash, 'error')) {
        this.errorObj = {
          status: 401,
          message: this.hash.error || 'Error parsing hash string'
        };
        return true;
      }

      return false;
    },
    isSsoRedirect() {
      const shouldRedirect = (
        has(this.hash, 'id_token')
        || has(this.hash, 'access_token')
        || has(this.hash, 'error')
      );

      log.debug({
        category: 'sso',
        message: 'isSsoRedirect',
        data: { shouldRedirect }
      });

      return shouldRedirect;
    },
    mergeAccounts() {
      // Merge with a new Auth0 primary identity, if this user is Google-SSO-only
      const mergeUri = process.env.TRIALS.V2_MERGE_URL
        .replace('{0}', this.getGoogleAuth0UserId())
        .replace('{1}', this.getEmail());

      return axios({
        method: 'put',
        url: mergeUri,
        headers: {
          'x-mat-tenant': this.getTenant(),
          Authorization: `Bearer ${this.hash.id_token}`
        }
      }) // api performs merge if necessary
        .then((mergeResponse) => {
          let idToken = this.hash.id_token;
          let accessToken = this.hash.id_token; // Hack

          if (mergeResponse.data && this.hasMergeTokens(mergeResponse.data)) {
            const { token } = mergeResponse.data;
            idToken = token.id_token;
            accessToken = token.id_token; // Hack
          }

          this.recordActivity(idToken, accessToken);
        })
        .catch(() => {
          this.recordActivity(this.hash.id_token, this.hash.id_token); // Hack
        });
    },
    recordActivity(idToken, accessToken) {
      let tenantId = 'unknown';
      let token;

      try {
        token = jwtDecode(idToken);
        tenantId = token['https://api.material.com/tenant'];
      } catch (e) {
        log.exception(e);
        this.redirect(idToken, accessToken);
        return;
      }

      const payload = {
        action: 'ssoLogin',
        tenantId,
        value: token.email
      };

      log.debug({
        category: 'sso',
        message: 'recordActivity',
        data: {
          payload
        }
      });

      axios.post(constants.v2.activity, payload)
        .then(() => {
          this.redirect(idToken, accessToken);
        }, ({ status, body }) => {
          log.warn({
            category: 'sso',
            message: `recordActivity.${status}`,
            data: { body }
          });
          this.redirect(idToken, accessToken);
        })
        .catch((error) => {
          log.exception(error);
          this.redirect(idToken, accessToken);
        });
    },
    redirect(idToken, accessToken) {
      log.debug({
        category: 'sso',
        message: 'redirect'
      });

      this.step = 'redirect';
      setcookie('id_token', idToken, 1, cookieDomain, false);
      setcookie('access_token', accessToken, 1, cookieDomain, false);
      window.location.href = constants.v2.admin;
    },
    ssoLoginFacebook() {
      log.debug({
        category: 'sso',
        message: 'ssoLoginFacebook'
      });

      this.auth0.authorize({
        connection: 'facebook'
      });
    },
    ssoLoginGoogle() {
      log.debug({
        category: 'sso',
        message: 'ssoLoginGoogle'
      });

      this.auth0.authorize({
        connection: 'google-oauth2'
      });
    },
    ssoLoginLinkedIn() {
      log.debug({
        category: 'sso',
        message: 'ssoLoginLinkedIn'
      });

      this.auth0.authorize({
        connection: 'linkedin'
      });
    }
  },
  beforeMount() {
    this.auth0 = new Auth0.WebAuth({
      clientID: constants.auth0.clientId,
      domain: constants.auth0.domain,
      grant_type: constants.auth0.grantType,
      redirectUri: window.location.href,
      responseType: 'token id_token',
      scope: constants.auth0.scope
    });

    if (this.isSsoRedirect()) {
      this.handleSsoRedirect();
    }
  }
};
